<script setup lang="ts">
/**
 * SkipLink - Skip to Main Content Link
 * 
 * WCAG 2.4.1: Bypass Blocks
 * Provides a way for keyboard users to skip navigation
 * and jump directly to main content.
 * 
 * Usage:
 * <SkipLink target="#main-content" />
 */

interface Props {
  target?: string
  label?: string
}

withDefaults(defineProps<Props>(), {
  target: '#main-content',
  // Fallback for standalone use; App.vue passes a localised label via i18n.
  label: 'Skip to main content',
})

function handleClick(event: MouseEvent, target: string) {
  event.preventDefault()
  
  const element = document.querySelector(target)
  if (element) {
    // Scroll to element
    element.scrollIntoView()
    
    // Set focus
    if (element instanceof HTMLElement) {
      // Add tabindex if needed
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '-1')
      }
      element.focus()
    }
  }
}
</script>

<template>
  <a
    :href="target"
    class="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    @click="handleClick($event, target)"
  >
    {{ label }}
  </a>
</template>
